(ns frontend.view.settings2.account-information.view
  (:require [common.currencies :as currencies]
            [common.date-formats :as date-formats]
            [common.localization :as loc]
            [common.timezones :as timezones]
            [frontend.actions.tenant :as tenant-actions]
            [frontend.db :as app-db]
            [frontend.logging :as logging]
            [frontend.page-components.default-page-layout :as default-page-layout]
            [frontend.routing :as routing]
            [frontend.state.current-session :as session]
            [frontend.utils :as fu :refer [format-data-size]]
            [frontend.view.components.clipboard :as clipboard]
            [frontend.view.not-found :as not-found]
            [frontend.view.settings2.account-information.db :as db]
            [frontend.view.settings2.account-information.events :as events]
            [reagent.core :as r]
            [ui.components.alert.view :as alert]
            [ui.components.toggle.view :as toggle]
            [ui.components.radix-ui.tooltip.view :as tooltip]
            [ui.editors.labelled-input.view :as labelled-input]
            [common.util :as cu]))

(defn company-name
  [{:keys [entity-atom tenant-id]}]
  (let [attribute :tenant/name
        old-value (get @entity-atom attribute)]
    [:div
     [labelled-input/view
      {:label (loc/tr :name)
       :data-type :data.type/text-simple
       :value     old-value
       :on-change (fn [new-value]
                    (swap! entity-atom assoc attribute new-value))
       :on-blur  (fn [new-value]
                   (tenant-actions/*->update!
                    tenant-id
                    attribute
                    new-value
                    (fn [_]
                      (logging/info "Update successful!"))
                    (fn [err]
                      (logging/error "Failed to update " err))))}]]))

(defn hubspot-id
  [{:keys [entity-atom tenant-id]}]
  (let [attribute :common/hubspot-id
        old-value (get @entity-atom attribute)]
    [:div
     [labelled-input/view
      {:label (loc/tr :common/hubspot-id)
       :data-type :data.type/text-simple
       :value     old-value
       :on-change (fn [new-value]
                    (swap! entity-atom assoc attribute new-value))
       :on-blur  (fn [new-value]
                   (tenant-actions/*->update!
                    tenant-id
                    attribute
                    new-value
                    (fn [_]
                      (logging/info "Update successful!"))
                    (fn [err]
                      (logging/error "Failed to update " err))))}]]))

(defn zapflow-email-inbox
  [{:keys [entity-atom tenant-id]}]
  (let [attribute :tenant.inbox/email
        old-value (get @entity-atom attribute)]
    [:div
     [labelled-input/view
      {:label (loc/tr :tenant.inbox/email)
       :data-type :data.type/text-simple
       :value     old-value
       :on-change (fn [new-value]
                    (swap! entity-atom assoc attribute new-value))
       :on-blur  (fn [new-value]
                   (tenant-actions/*->update!
                    tenant-id
                    attribute
                    new-value
                    (fn [_]
                      (logging/info "Update successful!"))
                    (fn [err]
                      (logging/error "Failed to update " err))))}]
     [:span {:class "text-xs"} (str old-value "@" (:inbound-email-domain @app-db/app-state))]]))

(defn account-id
  [{:keys [entity-atom]}]
  (let [value (get @entity-atom :db/id)]
    [:div
     {:class "flex flex-col gap-1.5"}
     [:label
      {:class "font-semibold text-sm m-0"}
      (loc/tr :settings/account-id)]
     [:div
      {:class "flex items-center gap-2"}
      [:span
       {:class "text-base-light-text"}
       (str value)]
      [tooltip/view
       [:button
        {:class    "p-1 text-base-light-text-paler hover:text-base-light-text"
         :disabled (nil? value)
         :on-click (fn [_]
                     (clipboard/copy-to (str value)))}
        [:i.fal.fa-copy]]
       [:span (loc/tr :copy-to-clipboard)]]]]))

(defn read-only-name
  [{:keys [entity-atom]}]
  (let [value (get @entity-atom :tenant/name)]
    [:div
     {:class "flex flex-col gap-1.5"}
     [:label
      {:class "font-semibold text-sm m-0"}
      (loc/tr :name)]
     [:div
      {:class "text-base-light-text"}
      value]]))

(defn account-details
  []
  (let [tenant-id (:db/id @session/tenant)
        entity-atom (r/cursor session/entities [tenant-id])]
    (fn []
      [:div
       [:div
        {:class "flex flex-col gap-4"}
        [:span
         {:class "font-bold text-xl"}
         (loc/tr :settings/account-details)]
        (if (session/zadmin?)
          [company-name
           {:entity-atom entity-atom
            :tenant-id   tenant-id}]
          [read-only-name
           {:entity-atom entity-atom}])
        [account-id
         {:entity-atom entity-atom
          :tenant-id   tenant-id}]]])))

(defn sys-admin-details
  []
  (let [tenant-id (:db/id @session/tenant)
        entity-atom (r/cursor session/entities [tenant-id])]
    (fn []
      [:div
       [:div
        {:class "flex flex-col gap-4"}
        [:span
         {:class "font-bold text-xl"}
         "Admin details"]
        [hubspot-id
         {:entity-atom entity-atom
          :tenant-id   tenant-id}]
        [zapflow-email-inbox
         {:entity-atom entity-atom
          :tenant-id   tenant-id}]]])))

(defn default-timezone
  [{:keys [entity-atom tenant-id]}]
  (let [attribute :tenant/timezone
        old-value (get @entity-atom attribute)
        options (->> timezones/timezones
                     (map (fn [[t l]]
                            {:value   t
                             :label l})))]
    [:div
     [labelled-input/view
      {:label (loc/tr :default-timezone)
       :data-type :data.type/ref-single
       :value     (->> options
                       (cu/filter-by :value old-value)
                       (first))
       :options options
       :on-change (fn [new-value]
                    (when-not (= new-value old-value)
                      (swap! entity-atom assoc attribute new-value)
                      (tenant-actions/*->update!
                       tenant-id
                       attribute
                       new-value
                       (fn [_]
                         (logging/info "Update successful!"))
                       (fn [err] ;;retract the optimistic save above
                         (when (= new-value (get @entity-atom attribute))
                           (swap! entity-atom assoc attribute old-value))
                         (logging/error "Failed to update " err)))))}]]))

(defn date-format
  [{:keys [entity-atom tenant-id]}]
  (let [attribute :tenant/date-format
        old-value (get @entity-atom attribute)
        options (->> date-formats/formats
                     (map (fn [k]
                            {:value   k
                             :label k})))]
    [:div
     [labelled-input/view
      {:label (loc/tr :default-dateformat)
       :data-type :data.type/ref-single
       :value     (->> options
                       (cu/filter-by :value old-value)
                       (first))
       :options options
       :on-change (fn [new-value]
                    (when-not (= new-value old-value)
                      (swap! entity-atom assoc attribute new-value)
                      (tenant-actions/*->update!
                       tenant-id
                       attribute
                       new-value
                       (fn [_]
                         (logging/info "Update successful!"))
                       (fn [err] ;;retract the optimistic save above
                         (when (= new-value (get @entity-atom attribute))
                           (swap! entity-atom assoc attribute old-value))
                         (logging/error "Failed to update " err)))))}]]))

(defn default-currency
  [{:keys [entity-atom tenant-id]}]
  (let [attribute :tenant/default-currency
        old-value (get @entity-atom attribute)
        options (->> currencies/currencies
                     (map (fn [[k l]]
                            {:value   k
                             :label l})))]
    [:div
     [labelled-input/view
      {:label (loc/tr :default-currency)
       :data-type :data.type/ref-single
       :value     (->> options
                       (cu/filter-by :value old-value)
                       (first))
       :options options
       :on-change (fn [new-value]
                    (when-not (= new-value old-value)
                      (swap! entity-atom assoc attribute new-value)
                      (tenant-actions/*->update!
                       tenant-id
                       attribute
                       new-value
                       (fn [_]
                         (logging/info "Update successful!"))
                       (fn [err] ;;retract the optimistic save above
                         (when (= new-value (get @entity-atom attribute))
                           (swap! entity-atom assoc attribute old-value))
                         (logging/error "Failed to update " err)))))}]]))

(defn default-multiplier
  [{:keys [entity-atom tenant-id]}]
  (let [attribute :tenant/cur-multiplier
        old-value (get @entity-atom attribute)
        options (->> currencies/multipliers
                     (map (fn [m]
                            {:value   m
                             :label m})))]
    [:div
     [labelled-input/view
      {:label (loc/tr :default-multiplier)
       :data-type :data.type/ref-single
       :value     (->> options
                       (cu/filter-by :value old-value)
                       (first))
       :options options
       :on-change (fn [new-value]
                    (when-not (= new-value old-value)
                      (swap! entity-atom assoc attribute new-value)
                      (tenant-actions/*->update!
                       tenant-id
                       attribute
                       new-value
                       (fn [_]
                         (logging/info "Update successful!"))
                       (fn [err] ;;retract the optimistic save above
                         (when (= new-value (get @entity-atom attribute))
                           (swap! entity-atom assoc attribute old-value))
                         (logging/error "Failed to update " err)))))}]]))

(defn decimal-places-for-currency-fields
  [{:keys [entity-atom tenant-id]}]
  (let [attribute :tenant/default-currency-decimal-places
        old-value (get @entity-atom attribute)]
    [:div
     [labelled-input/view
      {:label (loc/tr :decimal-places-for-currency-fields)
       :data-type :data.type/integer
       :value     old-value
       :on-change (fn [new-value]
                    (swap! entity-atom assoc attribute new-value))
       :on-blur  (fn [new-value]
                   (tenant-actions/*->update!
                    tenant-id
                    attribute
                    new-value
                    (fn [_]
                      (logging/info "Update successful!"))
                    (fn [err]
                      (logging/error "Failed to update " err))))}]]))

(defn primary-domain-for-email-addresses
  [{:keys [entity-atom tenant-id]}]
  (let [attribute :tenant/domain-primary
        old-value (get @entity-atom attribute)]
    [:div
     [labelled-input/view
      {:label (loc/tr :primary-domain-for-email-addresses)
       :data-type :data.type/text-simple
       :value     old-value
       :on-change (fn [new-value]
                    (swap! entity-atom assoc attribute new-value))
       :on-blur  (fn [new-value]
                   (tenant-actions/*->update!
                    tenant-id
                    attribute
                    new-value
                    (fn [_]
                      (logging/info "Update successful!"))
                    (fn [err]
                      (logging/error "Failed to update " err))))}]
     [:div {:class "pt-3"}
      [alert/warning
       {}
       [:span
        (loc/tr :settings/primary-domain-note)]]]]))

(defn account-settings
  []
  (let [tenant-id @session/tenant-id
        entity-atom (r/cursor session/entities [tenant-id])]
    (fn []
      [:div
       [:div
        [:span
         {:class "text-xl font-bold"}
         "Account settings"]
        [:div
         {:class "grid grid-cols-1 md:grid-cols-2 gap-4 pt-3"}
         [default-timezone
          {:entity-atom entity-atom
           :tenant-id   tenant-id}]
         [date-format
          {:entity-atom entity-atom
           :tenant-id   tenant-id}]
         [default-currency
          {:entity-atom entity-atom
           :tenant-id   tenant-id}]
         [default-multiplier
          {:entity-atom entity-atom
           :tenant-id   tenant-id}]
         [decimal-places-for-currency-fields
          {:entity-atom entity-atom
           :tenant-id   tenant-id}]
         [primary-domain-for-email-addresses
          {:entity-atom entity-atom
           :tenant-id   tenant-id}]]]])))

(defn storage-info
  [{:keys [occupied-bytes]}]
  [:div
   [:span
    (if-not (nil? occupied-bytes)
      (->> occupied-bytes
           (format-data-size)
           (vector)
           (loc/tr :data-used))
      (loc/tr :loading))]])

(defn data-storage
  []
  (let [_ (events/load-data-storage)]
    (fn []
      (let [{:keys [data-storage]} (:resources-limitation @db/state)]
        [:div
         {:class-name "pt-4"}
         [:div
          [:div
           {:class "flex flex-col gap-4"}
           [:span
            {:class "font-bold text-xl"}
            (loc/tr :settings/data-storage)]
           [storage-info data-storage]]]]))))

(defn application-analytics-support
  []
  (fn []
    [:div
     [:div
      {:class "flex justify-between items-center"}
      [:span (loc/tr :feature/application-analytics)]

      [tooltip/view
       [toggle/toggle
        {:checked?  true  ;;TODO why was this hardcoded to be always checked and disabled? Not needed or to be added later?
         :disabled? true
         :on-change  (fn [_])}]
       [:span
        (loc/tr :application-analytics-tooltip)]]]]))

(defn component
  []
  (r/create-class
   {:component-did-mount
    (fn [_])
    :component-will-unmount
    (fn [_])
    :reagent-render
    (fn []
      [:<>
       [:div
        {:class "col-span-8 flex flex-col gap-8"}
        [account-details]
        (when (session/sys-admin?)
          [sys-admin-details])
        [account-settings]

        [:div
         {:class "flex flex-col gap-4"}
         [:span
          {:class "font-bold text-xl"}
          (loc/tr :legal)]
         [:span
          {:class "flex gap-3"}
          (loc/tr :download)
          [:a
           {:href "https://static.zapflow.com/terms/Terms%20of%20services%20Zapflow.pdf"
            :download "Terms of service"
            :target "_blank"
            :class "underline hover:no-underline"}
           (loc/tr :general-terms-of-service)]]]

        (when (session/sys-admin?)
          [:div
           {:class "flex flex-col gap-6"}
           [data-storage]])

        (when (session/has-feature-access? :feature/application-analytics)
          [application-analytics-support])]])}))

(defmethod routing/render-view :new-settings/account-information
  [_]
  (if (session/is-admin?)
    [default-page-layout/view
     [component]]
    [not-found/not-found-view]))
